/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router-dom";
import { StatusTag } from "components/StatusTag";
import {
  ContainerV2,
  ContainerView,
  DesktopContainer,
  MobileContainer,
} from "../../../components/Container";
import { TitleWithBack } from "../../../components/Layout";
import { useApp } from "../../../contexts";
import { ContainerTableStakeMobile, TextCellNormal } from "../styles";
import NumberFormat from "react-number-format";
import { dateFormatNumber } from "../../../utils/helper";
import { STAKE_SUB_TAB } from "./TableStake";
import { Copy } from "../../../components/Copy";
import { CopyIcon } from "../../../components/Icons/CopyIcon";
import { ContainerTable, TableView } from "../../../components/Table";
import { useState } from "react";
import { WalletRowStake } from "./WalletRowStake";
import { LoadingList } from "../../../components/Loading2";
import { FilterStake } from "./FilterStake";
import { FilterIcon } from "../../../components/Icons/FilterIcon";
import { useWindowSize } from "../../../hook/useWindowSize";
import moment from "moment";
import { getStakeTransactions } from "../../../services/app";
import { useEffect } from "react";
import EmptyTable from "../../../components/EmptyTable";
import EmptyTableMobile from "../../../components/EmptyTableMobile";
import {
  decimalSeparatorByCurrency,
  thousandSeparatorByCurrency,
} from "../../../utils/Currency";

const VIEW_TYPE = STAKE_SUB_TAB.HISTORY;

const PAGE_SIZE = 1000;

const SORT_BY = {
  amount: "amount",
  startTime: "start_date",
  claimTime: "end_date",
};

const SORT_TYPE = {
  ascend: "asc",
  descend: "desc",
};

export const StakeHistory = () => {
  const { T, getCurrency } = useApp();
  const navigate = useNavigate();
  const size = useWindowSize();
  const isMobile = size.width < 768;
  const title = T("Staking History");

  const [rows, setRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filterParams, setFilterParams] = useState({});
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    loadData();
  }, [filterParams.sortBy, filterParams.sortType]);

  const handleSearch = () => {
    loadData();
  };

  const handleReset = () => {
    setFilterParams({
      status: "",
    });
    setTableKey((prev) => prev + 1);
    loadData();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { order, field } = sorter;
    setFilterParams({
      ...filterParams,
      sortBy: field,
      sortType: order,
    });
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const loadData = async (page = 1) => {
    try {
      setTableLoading(true);
      const result = await fetchDataHistory({ page });
      if (result) {
        const items = result.map((item) => ({
          key: item.stakeRecordId,
          ...item,
        }));
        setRows(items);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.log(err);
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchDataHistory = async ({ page }) => {
    const params = {
      status: filterParams?.status,
      transactionId: filterParams?.transactionId,
      endDateFrom: formatFromDateParam(filterParams?.endDateRange?.[0]),
      endDateTo: formatToDateParam(filterParams?.endDateRange?.[1]),
      startDateFrom: formatFromDateParam(filterParams?.startDateRange?.[0]),
      startDateTo: formatToDateParam(filterParams?.startDateRange?.[1]),
      sortBy: SORT_BY[filterParams?.sortBy] ?? SORT_BY.startTime,
      sortType: SORT_TYPE[filterParams?.sortType] ?? SORT_TYPE.descend,
      pageSize: PAGE_SIZE,
      page: page,
    };

    try {
      const res = await getStakeTransactions(params);
      const result = res?.data?.records;
      return result;
    } catch (err) {
      console.log("TableStake -> fetchDataActive -> err", err);
    }
  };

  const handleBackClick = () => {
    navigate("/stake-for-option");
  };

  const formatFromDateParam = (date) => {
    return date ? moment(date).startOf("day").toISOString() : null;
  };

  const formatToDateParam = (date) => {
    return date ? moment(date).endOf("day").toISOString() : null;
  };

  const navigateToStakeDetail = (item) => {
    navigate(`/stake-by-option-detail?stakeRecordId=${item.stakeRecordId}`);
  };

  const columns = [
    {
      title: T("Transaction ID"),
      dataIndex: "txHash",
      key: "txHash",
      align: "center",
      width: 200,
      render: (text, record, index) =>
        text ? (
          <div className="w-[140px] mx-auto">
            <Copy
              textStyle={{ fontWeight: 400, fontSize: 16 }}
              cutText={true}
              splitFont={5}
              splitFontLg={5}
              numDot={3}
              text={text}
              icon={<CopyIcon />}
            />
          </div>
        ) : (
          <></>
        ),
    },
    {
      title: T("Stake Amount"),
      dataIndex: "amount",
      key: "amount",
      align: "right",
      className: "justify-end",
      width: 150,
      fixed: "left",
      sorter: true,
      render: (text, record, index) => (
        <TextCellNormal>
          <NumberFormat
            value={record.amount}
            displayType={"text"}
            thousandSeparator={thousandSeparatorByCurrency(getCurrency())}
            decimalSeparator={decimalSeparatorByCurrency(getCurrency())}
            suffix={` ${record.tokenName}`}
            decimalScale={3}
          />
        </TextCellNormal>
      ),
    },
    {
      title: T("Duration"),
      dataIndex: "duration",
      key: "duration",
      align: "right",
      width: 150,
      fixed: "left",
      render: (text, record, index) => (
        <TextCellNormal>
          <span className="capitalize">
            {record.duration} {record.duration > 1 ? T(record.durationUnit + 's') : T(record.durationUnit)}
          </span>
        </TextCellNormal>
      ),
    },
    {
      title: T("Status"),
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 130,
      render: (status, record, index) => {
        return (
          <StatusTag status={status} />
        );
      },
    },
    {
      title: T("Time"),
      dataIndex: "startTime",
      key: "startTime",
      align: "center",
      className: "justify-center",
      width: 150,
      sorter: true,
      render: (text, record, index) => (
        <TextCellNormal>{dateFormatNumber(text, "YYYY/MM/DD")}</TextCellNormal>
      ),
    },
  ];

  const renderReloadingIcon = () => {
    return (
      <div className="w-full flex justify-center pt-6">
        <LoadingList />
      </div>
    );
  };

  const renderFilter = () => {
    return (
      <>
        <div className="flex items-center justify-end lg:justify-between pt-0">
          <div className="block lg:hidden">
            <div
              className={`flex justify-center items-center px-[14px] py-[10px] 
                  bg-white gap-[8px] rounded-full lg:rounded-[10px] cursor-pointer`}
              onClick={() => toggleFilter()}
            >
              <span>
                {" "}
                <FilterIcon />{" "}
              </span>
              <span className={`text-sm text-black`}>{T("Filter")}</span>
            </div>
          </div>
        </div>

        <FilterStake
          show={showFilter || !isMobile}
          isMobile={isMobile}
          viewType={VIEW_TYPE}
          filterParams={filterParams}
          onUpdate={setFilterParams}
          onSearch={handleSearch}
          onReset={handleReset}
          onClose={() => {
            setShowFilter(false);
          }}
        />
      </>
    );
  };

  const renderList = () => {
    return (
      <>
        <div className="mt-[20px] lg:mt-[20px]">
          <DesktopContainer>
            <ContainerTable>
              <TableView
                key={tableKey}
                locale={{ emptyText: <EmptyTable T={T} /> }}
                scroll={{ x: true }}
                tableLayout={"fixed"}
                pagination={false}
                dataSource={rows}
                columns={columns}
                loading={tableLoading}
                isClickRow
                onRow={(record, rowIndex) => {
                  return {
                    onClick: (event) => {},
                  };
                }}
                onChange={handleTableChange}
              />
            </ContainerTable>
          </DesktopContainer>

          <MobileContainer>
            {tableLoading ? (
              <>{renderReloadingIcon()}</>
            ) : (
              <>
                {rows?.length ? (
                  rows?.map((item, index) => (
                    <ContainerTableStakeMobile key={index}>
                      <WalletRowStake
                        viewType={VIEW_TYPE}
                        item={item}
                        onClick={() => navigateToStakeDetail(item)}
                      />
                    </ContainerTableStakeMobile>
                  ))
                ) : (
                  <div className="w-full flex justify-center pt-[10px]">
                    <EmptyTableMobile T={T} />
                  </div>
                )}
              </>
            )}
          </MobileContainer>
        </div>
      </>
    );
  };

  return (
    <>
      <ContainerV2>
        <ContainerView className="">
          <TitleWithBack
            className="mt-7"
            title={title}
            onClick={handleBackClick}
          />
          {renderFilter()}
          {renderList()}
        </ContainerView>
      </ContainerV2>
    </>
  );
};
